"use client";

import { useState } from "react";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import {
  type CsvDelimiterChoice,
  type CsvDelimiterName,
  csvDelimiterName,
  parseCsvRows,
  resolveCsvDelimiter,
} from "@/lib/tool-kit/developer/csv-table";

type Delimiter = CsvDelimiterChoice;

function buildHtmlTable(rows: string[][]): string {
  if (rows.length === 0) {
    return "";
  }
  const header = rows[0];
  const body = rows.slice(1);

  const thCells = header
    .map(
      (h) =>
        `<th style="border:1px solid #ccc;padding:8px 12px;background:#f5f5f5;font-weight:600;text-align:left">${escapeHtml(h)}</th>`
    )
    .join("");
  const headerRow = `<tr>${thCells}</tr>`;

  const bodyRows = body
    .map((row) => {
      const tds = row
        .map(
          (cell) =>
            `<td style="border:1px solid #ccc;padding:8px 12px">${escapeHtml(cell)}</td>`
        )
        .join("");
      return `<tr>${tds}</tr>`;
    })
    .join("\n");

  return `<table style="border-collapse:collapse;width:100%;font-family:sans-serif;font-size:14px">\n<thead>\n${headerRow}\n</thead>\n<tbody>\n${bodyRows}\n</tbody>\n</table>`;
}

function escapeHtml(str: string): string {
  return str
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const CSV_TO_TABLE_STRINGS = {
  delimiter: "Delimiter",
  csvInput: "CSV input",
  nameAgeCityAlice30: "Name,Age,City\nAlice,30,New York\nBob,25,London",
  convertToTable: "Convert to Table",
  copied: "Copied!",
  copyHtml: "Copy HTML",
  copyCsv: "Copy CSV",
  clear: "Clear",
  dataRows: "Data rows",
  columns: "Columns",
  tablePreview: "Table Preview",
  delimiterLabels: {
    auto: "Auto-detect",
    comma: "Comma (,)",
    tab: "Tab",
    semicolon: "Semicolon (;)",
  },
  errorNoData: "No data found. Make sure your CSV has at least one row.",
  errorParseFailed: "Failed to parse CSV. Please check your input.",
} as const;

type Strings = typeof CSV_TO_TABLE_STRINGS;

function getDelimiterLabel(
  d: Delimiter | CsvDelimiterName,
  strings: Strings
): string {
  return strings.delimiterLabels[d];
}

export default function CsvToTableWidget() {
  const [input, setInput] = useState("");
  const [delimiter, setDelimiter] = useState<Delimiter>("auto");
  const [rows, setRows] = useState<string[][] | null>(null);
  const [usedDelimiter, setUsedDelimiter] = useState<CsvDelimiterName>("comma");
  const [error, setError] = useState("");
  const [copiedHtml, setCopiedHtml] = useState(false);
  const [copiedCsv, setCopiedCsv] = useState(false);

  const strings = useToolUi(CSV_TO_TABLE_STRINGS);

  const fmt = useToolNumber();

  function handleConvert() {
    if (input.trim().length === 0) {
      return;
    }
    try {
      const delimChar = resolveCsvDelimiter(delimiter, input);
      const parsed = parseCsvRows(input, delimChar);
      if (parsed.length === 0) {
        setError(strings.errorNoData);
        setRows(null);
        return;
      }
      setError("");
      setUsedDelimiter(csvDelimiterName(delimChar));
      setRows(parsed);
    } catch {
      setError(strings.errorParseFailed);
      setRows(null);
    }
  }

  function handleClear() {
    setInput("");
    setRows(null);
    setError("");
    setCopiedHtml(false);
    setCopiedCsv(false);
  }

  function handleCopyHtml() {
    if (!rows) {
      return;
    }
    const html = buildHtmlTable(rows);
    writeToClipboard(html).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedHtml(true);
      setTimeout(() => setCopiedHtml(false), 2000);
    });
  }

  function handleCopyCsv() {
    writeToClipboard(input).then((copied) => {
      if (!copied) {
        return;
      }

      setCopiedCsv(true);
      setTimeout(() => setCopiedCsv(false), 2000);
    });
  }

  const rowCount = rows ? rows.length - 1 : 0;
  const colCount = rows ? (rows[0]?.length ?? 0) : 0;

  const delimLabel = getDelimiterLabel(usedDelimiter, strings);

  return (
    <>
      {/* Delimiter selector */}
      <section className="space-y-2">
        <p className="font-medium text-foreground text-sm">
          {strings.delimiter}
        </p>
        <div className="flex flex-wrap gap-3">
          {(["auto", "comma", "tab", "semicolon"] as Delimiter[]).map((d) => (
            <label
              className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5"
              key={d}
            >
              <input
                checked={delimiter === d}
                className="accent-primary"
                name="delimiter"
                onChange={() => setDelimiter(d)}
                type="radio"
                value={d}
              />
              {getDelimiterLabel(d, strings)}
            </label>
          ))}
        </div>
      </section>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="csv-input"
        >
          {strings.csvInput}
        </label>
        <textarea
          className="min-h-[180px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="csv-input"
          onChange={(e) => {
            setInput(e.target.value);
            setRows(null);
            setError("");
          }}
          placeholder={strings.nameAgeCityAlice30}
          value={input}
        />
      </div>
      {/* Error */}
      {error.length > 0 && (
        <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-2 text-red-700 text-sm dark:text-red-300">
          {error}
        </p>
      )}
      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleConvert}
          type="button"
        >
          {strings.convertToTable}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={!rows}
          onClick={handleCopyHtml}
          type="button"
        >
          {copiedHtml ? strings.copied : strings.copyHtml}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.trim().length === 0}
          onClick={handleCopyCsv}
          type="button"
        >
          {copiedCsv ? strings.copied : strings.copyCsv}
        </button>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={input.length === 0}
          onClick={handleClear}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      {/* Stats */}
      {rows !== null && (
        <div className="grid grid-cols-3 gap-4">
          <StatCard label={strings.dataRows} value={fmt(rowCount)} />
          <StatCard label={strings.columns} value={colCount} />
          <StatCard label={strings.delimiter} value={delimLabel} />
        </div>
      )}
      {/* Table preview */}
      {rows !== null && rows.length > 0 && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.tablePreview}
          </h2>
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              {rows[0] && (
                <thead className="bg-muted/50">
                  <tr>
                    {rows[0].map((cell) => (
                      <th
                        className="border-border border-b px-4 py-2 text-left font-medium text-foreground"
                        key={cell}
                      >
                        {cell}
                      </th>
                    ))}
                  </tr>
                </thead>
              )}
              <tbody>
                {rows.slice(1).map((row) => (
                  <tr
                    className="border-border border-t bg-card odd:bg-card even:bg-muted/20"
                    key={row.join("\x00")}
                  >
                    {row.map((cell, ci) => (
                      <td
                        className="px-4 py-2 text-muted-foreground"
                        key={`${rows[0]?.[ci] ?? ci}-${cell}`}
                      >
                        {cell}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );
}
